import React, { useEffect, useState } from 'react';
import {
   ShoppingCart, CheckCircle2, ChevronDown, ChevronUp, Box, CreditCard,
   Smartphone, Search, ShieldCheck, Zap, Clock, Sparkles,
   Users, TrendingUp, Globe, Package, FileText, MonitorSmartphone, Code,
   Rocket, Headphones, Layout, FileCode, Flame, Database, Layers, Palette, 
   Terminal, Server, Cloud, Shirt, Gem, Coffee, BookOpen, Truck, Briefcase
} from 'lucide-react';
import SEO from './SEO';

const FAQItem = ({ question, answer }) => {
   const [isOpen, setIsOpen] = useState(false);
   return (
      <div className="border border-white/10 rounded-2xl bg-white/5 overflow-hidden mb-4 transition-all duration-300 hover:border-[#0EA5E9]/30">
         <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-6 py-5 flex items-center justify-between font-semibold text-lg text-left hover:text-[#0EA5E9] transition-colors"
         >
            <span>{question}</span>
            {isOpen ? <ChevronUp className="w-5 h-5 text-[#0EA5E9]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
         </button>
         <div className={`px-6 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
            <p className="text-gray-400 text-lg leading-relaxed">{answer}</p>
         </div>
      </div>
   );
};

const EcommerceWebsite = () => {
   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="bg-[#0A0F1F] min-h-screen text-white pt-24 selection:bg-[#0EA5E9] selection:text-white" style={{ fontFamily: 'emoji' }}>
         <SEO
           title="Ecommerce Website Development in India – Vishwa DigiTech"
           description="Get a powerful ecommerce website that sells 24x7. Vishwa DigiTech builds online stores with secure payment gateway, inventory management & mobile-friendly design. Start selling online today."
           keywords="ecommerce website development india, online store development sangli, shopify development india, woocommerce website, ecommerce website design maharashtra, sell online india"
           canonicalPath="/services/ecommerce"
         />

         {/* 1. Hero Banner */}
         <section className="relative py-20 lg:py-32 overflow-hidden">
            <div className="absolute top-0 right-0 w-full h-[800px] bg-gradient-to-br from-[#0EA5E9]/20 via-purple-500/10 to-transparent blur-[150px] -z-10" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 mb-8 backdrop-blur-sm">
                  <ShoppingCart className="w-4 h-4 text-[#0EA5E9]" />
                  <span className="text-sm font-bold tracking-widest text-[#0EA5E9] uppercase">Ecommerce Website Development</span>
               </div>

               <h1 className="text-5xl md:text-7xl font-black mb-8 leading-[1.1] tracking-tight">
                  Build a Powerful Online Store <br className="hidden md:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">That Sells 24×7.</span>
               </h1>

               <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-4xl mx-auto leading-relaxed">
                  Launch a professional ecommerce website that helps you sell products online, manage inventory, receive secure online payments, and grow your business across India.
               </p>

               <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-blue-600 hover:from-blue-600 hover:to-[#0EA5E9] rounded-xl font-bold text-lg transition-all shadow-[0_0_30px_rgba(14,165,233,0.3)] hover:scale-105">
                     Get Free Consultation
                  </button>
                  <button className="px-8 py-4 border border-white/20 hover:bg-white/10 rounded-xl font-bold text-lg transition-all">
                     Request Demo
                  </button>
               </div>
            </div>
         </section>

         {/* 2. Why Your Business Needs */}
         <section className="py-20 bg-black/30 border-y border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div className="relative rounded-[2rem] overflow-hidden border border-white/10 group">
                     <div className="absolute inset-0 bg-[#0EA5E9]/20 opacity-0 group-hover:opacity-100 transition-opacity z-10" />
                     <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=1000" alt="Ecommerce Analytics" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>

                  <div className="space-y-8">
                     <h2 className="text-4xl md:text-5xl font-bold">Why Choose <span className="text-[#0EA5E9]">Ecommerce?</span></h2>
                     <div className="p-6 bg-gradient-to-r from-blue-900/40 to-purple-900/20 border border-blue-500/30 rounded-2xl">
                        <p className="text-xl font-medium text-white mb-2">आज प्रत्येक ग्राहक Online Shopping करतो.</p>
                        <p className="text-gray-300">जर तुमचा Business Online नसेल तर अनेक Potential Customers तुम्ही गमावत आहात.</p>
                     </div>

                     <div>
                        <h3 className="text-2xl font-bold mb-6 text-gray-200">Ecommerce Website मुळे:</h3>
                        <ul className="space-y-4">
                           {[
                              "Sell Products 24/7",
                              "Reach Customers Across India",
                              "Secure Online Payments",
                              "Easy Product Management",
                              "Increase Sales"
                           ].map((item, idx) => (
                              <li key={idx} className="flex items-center gap-4 text-xl text-gray-300 font-medium">
                                 <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                 </div>
                                 {item}
                              </li>
                           ))}
                        </ul>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* 3. Features Section */}
         <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
               <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Ecommerce <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Features</span></h2>
               <p className="text-xl text-gray-400">Everything you need to run a successful online business.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <Package className="w-10 h-10 text-[#0EA5E9] mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">Product Management</h3>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" /> Unlimited Products</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" /> Categories</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" /> Stock Management</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" /> Images & Videos</li>
                  </ul>
               </div>

               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <ShoppingCart className="w-10 h-10 text-purple-400 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">Shopping Cart</h3>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-purple-400" /> Add to Cart</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-purple-400" /> Wishlist</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-purple-400" /> Coupon</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-purple-400" /> Checkout</li>
                  </ul>
               </div>

               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <CreditCard className="w-10 h-10 text-green-400 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">Payment Gateway</h3>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-green-400" /> Razorpay</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-green-400" /> Stripe & PayPal</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-green-400" /> UPI</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-green-400" /> Credit/Debit Cards</li>
                  </ul>
               </div>

               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <FileText className="w-10 h-10 text-blue-400 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">Order Management</h3>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Order Tracking</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Invoice</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Order Status</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Customer Details</li>
                  </ul>
               </div>

               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <MonitorSmartphone className="w-10 h-10 text-pink-400 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">Mobile Responsive</h3>
                  <p className="text-gray-400 mb-2">Works perfectly on:</p>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-pink-400" /> Desktop</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-pink-400" /> Tablet</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-pink-400" /> Mobile</li>
                  </ul>
               </div>

               <div className="bg-white/5 border border-white/10 p-8 rounded-3xl hover:border-[#0EA5E9]/50 transition-colors group">
                  <Search className="w-10 h-10 text-yellow-400 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-2xl font-bold mb-4">SEO Friendly</h3>
                  <ul className="space-y-2 text-gray-400">
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-yellow-400" /> Fast Loading</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-yellow-400" /> Google Friendly</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-yellow-400" /> Schema</li>
                     <li className="flex gap-2 items-center"><CheckCircle2 className="w-4 h-4 text-yellow-400" /> Meta Tags</li>
                  </ul>
               </div>
            </div>
         </section>

         {/* 4. Solutions We Build */}
         <section className="py-24 bg-[#0A0F1F] relative overflow-hidden border-y border-white/5">
            {/* Background elements */}
            <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[#0EA5E9]/5 rounded-full blur-[150px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md animate-text-reveal">
                 <Box className="w-4 h-4" />
                 Industry Specific
               </div>
               
               <h2 className="text-4xl md:text-5xl font-black mb-16 tracking-tight">
                  Ecommerce <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Solutions We Build</span>
               </h2>
               
               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {[
                     { name: "Fashion Store", icon: Shirt, color: "text-pink-400", bg: "group-hover:bg-pink-500/10", border: "group-hover:border-pink-500/50" },
                     { name: "Electronics", icon: MonitorSmartphone, color: "text-blue-400", bg: "group-hover:bg-blue-500/10", border: "group-hover:border-blue-500/50" },
                     { name: "Jewellery", icon: Gem, color: "text-amber-400", bg: "group-hover:bg-amber-500/10", border: "group-hover:border-amber-500/50" },
                     { name: "Furniture", icon: Box, color: "text-orange-400", bg: "group-hover:bg-orange-500/10", border: "group-hover:border-orange-500/50" },
                     { name: "Grocery", icon: ShoppingCart, color: "text-green-400", bg: "group-hover:bg-green-500/10", border: "group-hover:border-green-500/50" },
                     { name: "Medical", icon: ShieldCheck, color: "text-emerald-400", bg: "group-hover:bg-emerald-500/10", border: "group-hover:border-emerald-500/50" },
                     { name: "Restaurant", icon: Coffee, color: "text-rose-400", bg: "group-hover:bg-rose-500/10", border: "group-hover:border-rose-500/50" },
                     { name: "Cosmetics", icon: Sparkles, color: "text-fuchsia-400", bg: "group-hover:bg-fuchsia-500/10", border: "group-hover:border-fuchsia-500/50" },
                     { name: "Book Store", icon: BookOpen, color: "text-indigo-400", bg: "group-hover:bg-indigo-500/10", border: "group-hover:border-indigo-500/50" },
                     { name: "Wholesale", icon: Truck, color: "text-cyan-400", bg: "group-hover:bg-cyan-500/10", border: "group-hover:border-cyan-500/50" },
                     { name: "B2B Portal", icon: Briefcase, color: "text-purple-400", bg: "group-hover:bg-purple-500/10", border: "group-hover:border-purple-500/50" },
                     { name: "Custom", icon: Code, color: "text-sky-400", bg: "group-hover:bg-sky-500/10", border: "group-hover:border-sky-500/50" }
                  ].map((store, idx) => (
                     <div key={idx} className={`group flex flex-col items-center justify-center text-center p-6 md:p-8 bg-white/5 border border-white/10 rounded-3xl transition-all duration-500 cursor-default shadow-lg hover:shadow-2xl hover:-translate-y-2 ${store.border} ${store.bg} relative overflow-hidden`}>
                        
                        {/* Soft spotlight behind icon */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white/5 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                        
                        <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#0A0F1F] border border-white/10 flex items-center justify-center mb-4 md:mb-5 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 shadow-inner z-10`}>
                           <store.icon className={`w-7 h-7 md:w-8 md:h-8 ${store.color}`} />
                        </div>
                        
                        <h3 className="text-white font-bold text-sm md:text-lg tracking-wide group-hover:text-white transition-colors z-10">
                           {store.name}
                        </h3>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 5. Development Process (Alternating Timeline) */}
         <section className="py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0EA5E9]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[150px] pointer-events-none" />

            <div className="text-center mb-24 relative z-10 flex flex-col items-center">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md animate-text-reveal">
                 <Sparkles className="w-4 h-4" />
                 How We Build
               </div>
               <h2 className="text-4xl md:text-6xl font-black mb-6 text-center">Our Development <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Process</span></h2>
               <p className="text-gray-400 text-xl max-w-3xl mx-auto font-light">From idea to launch, our streamlined process ensures your ecommerce store is built for scale, speed, and sales.</p>
            </div>
            
            <div className="relative z-10 max-w-5xl mx-auto">
               {/* Central glowing line (hidden on small screens, shown on md and up) */}
               <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0EA5E9]/10 via-[#0EA5E9]/50 to-[#0EA5E9]/10 -translate-x-1/2 rounded-full"></div>
               {/* Mobile line (left aligned) */}
               <div className="block md:hidden absolute left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0EA5E9]/10 via-[#0EA5E9]/50 to-[#0EA5E9]/10 -translate-x-1/2 rounded-full"></div>

               <div className="space-y-12 md:space-y-24">
                  {[
                     { step: "01", title: "Requirement Analysis", desc: "We dive deep into your business model, catalog size, and target audience to map out the perfect ecommerce strategy.", icon: Search },
                     { step: "02", title: "UI/UX Design", desc: "Our designers craft high-converting, mobile-first interfaces that make browsing and buying effortless for your customers.", icon: Smartphone },
                     { step: "03", title: "Development", desc: "We write clean, scalable code integrating secure payment gateways, inventory management, and fast-loading pages.", icon: Code },
                     { step: "04", title: "Testing & QA", desc: "Rigorous cross-device testing, security audits, and load testing ensure a bug-free launch day.", icon: ShieldCheck },
                     { step: "05", title: "Deployment", desc: "We deploy your store on scalable cloud infrastructure with zero downtime and configure all analytics.", icon: Rocket },
                     { step: "06", title: "Growth & Support", desc: "Post-launch, we provide 24/7 monitoring, security updates, and performance optimization to fuel your growth.", icon: Headphones },
                  ].map((item, idx) => {
                     const isEven = idx % 2 === 1;
                     return (
                        <div key={idx} className={`relative flex flex-col md:flex-row md:items-center group ${isEven ? 'md:flex-row-reverse' : ''}`}>
                           
                           {/* Timeline Node (Center on Desktop, Left on Mobile) */}
                           <div className="absolute top-1/2 -translate-y-1/2 left-6 md:left-1/2 w-10 h-10 md:w-12 md:h-12 bg-[#0A0F1F] border-4 border-[#0EA5E9] rounded-full -translate-x-1/2 flex items-center justify-center z-20 shadow-[0_0_20px_rgba(14,165,233,0.5)] group-hover:scale-125 group-hover:bg-[#0EA5E9] transition-all duration-500">
                              <span className="text-white font-bold text-xs md:text-sm group-hover:hidden">{idx + 1}</span>
                              <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-white hidden group-hover:block" />
                           </div>

                           {/* Spacer for empty half on Desktop */}
                           <div className="hidden md:block w-1/2"></div>

                           {/* Content Card */}
                           <div className={`w-full md:w-1/2 pl-16 md:pl-0 ${isEven ? 'md:pr-16 lg:pr-24 text-left md:text-right' : 'md:pl-16 lg:pl-24 text-left'}`}>
                              <div className="bg-[#0F1629]/80 backdrop-blur-md border border-white/5 rounded-2xl md:rounded-3xl p-6 md:p-8 hover:border-[#0EA5E9]/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_15px_40px_-10px_rgba(14,165,233,0.25)] relative overflow-hidden">
                                 {/* Glowing accent corner */}
                                 <div className={`absolute top-0 ${isEven ? 'left-0' : 'right-0'} w-32 h-32 bg-[#0EA5E9]/10 rounded-full blur-3xl group-hover:bg-[#0EA5E9]/20 transition-all duration-500`} />
                                 
                                 <div className={`flex items-center gap-4 mb-4 md:mb-6 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#0EA5E9]/20 to-transparent border border-[#0EA5E9]/20 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500 shadow-lg">
                                       <item.icon className="w-6 h-6 md:w-8 md:h-8 text-[#0EA5E9]" />
                                    </div>
                                    <div>
                                       <div className="text-[#0EA5E9] font-black tracking-widest text-xs md:text-sm mb-1 uppercase">Step {item.step}</div>
                                       <h3 className="text-xl md:text-2xl font-bold text-white">{item.title}</h3>
                                    </div>
                                 </div>
                                 
                                 <p className="text-gray-400 leading-relaxed text-base md:text-lg font-light">{item.desc}</p>
                              </div>
                           </div>

                        </div>
                     );
                  })}
               </div>
            </div>
         </section>

         {/* 6. Why Choose Vishwa & 8. Benefits */}
         <section className="py-20 bg-black/40 border-y border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid lg:grid-cols-2 gap-16">

                  <div>
                     <h2 className="text-4xl font-bold mb-10">Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Vishwa DigiTech</span></h2>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                           "Professional Design", "Fast Performance", "SEO Ready",
                           "Secure Website", "Responsive Design", "Admin Panel",
                           "Payment Gateway", "WhatsApp Integration", "Google Analytics", "Free Support"
                        ].map((feature, idx) => (
                           <div key={idx} className="flex items-center gap-3 bg-white/5 border border-white/5 p-4 rounded-xl hover:border-[#0EA5E9]/30 transition-colors">
                              <CheckCircle2 className="w-5 h-5 text-[#0EA5E9]" />
                              <span className="font-semibold">{feature}</span>
                           </div>
                        ))}
                     </div>
                  </div>

                  <div>
                     <h2 className="text-4xl font-bold mb-10">Benefits <span className="text-purple-400">You'll Get</span></h2>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                           { title: "Increase Online Sales", icon: TrendingUp },
                           { title: "Grow Brand", icon: Globe },
                           { title: "24×7 Store", icon: Clock },
                           { title: "Easy Inventory", icon: Box },
                           { title: "Online Payments", icon: CreditCard },
                           { title: "Customer Database", icon: Users },
                           { title: "Marketing Ready", icon: Zap },
                           { title: "Better Customer Experience", icon: ShieldCheck }
                        ].map((benefit, idx) => (
                           <div key={idx} className="flex items-center gap-4 bg-gradient-to-r from-purple-900/20 to-transparent border border-purple-500/20 p-4 rounded-xl">
                              <benefit.icon className="w-6 h-6 text-purple-400" />
                              <span className="font-semibold">{benefit.title}</span>
                           </div>
                        ))}
                     </div>
                  </div>

               </div>
            </div>
         </section>

         {/* 7. Technologies We Use */}
         {/* <section className="py-16 overflow-hidden bg-[#0A0F1F]">
            <div className="text-center mb-10 flex flex-col items-center">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md animate-text-reveal">
                 <Sparkles className="w-4 h-4" />
                 Our Technologies
               </div>
               <h2 className="text-4xl md:text-5xl font-bold flex items-center justify-center gap-3">
                 <Code className="w-8 h-8 text-[#0EA5E9]" />
                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Technologies We Use</span>
               </h2>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8 max-w-5xl mx-auto opacity-70">
               {[
                  "React JS", "PHP", "Firebase", "MySQL", "Bootstrap",
                  "HTML5", "CSS3", "JavaScript", "Node JS", "Razorpay", "Cloud Hosting"
               ].map((tech, idx) => (
                  <div key={idx} className="text-xl font-black tracking-wider px-4">
                     {tech}
                  </div>
               ))}
            </div>
         </section> */}

         <section className="relative py-20 bg-[#0A0F1F] overflow-hidden">

  {/* Background Blur */}
  <div className="absolute top-10 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
  <div className="absolute bottom-10 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>

  <div className="relative max-w-7xl mx-auto px-6">

    {/* Heading */}
    <div className="text-center mb-16">
      <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-400 text-sm font-bold uppercase mb-6">
        <Sparkles className="w-4 h-4" />
        Our Technologies
      </div>

      <h2 className="text-4xl md:text-5xl font-extrabold">
        <span className="text-white">Technologies</span>{" "}
        <span className="bg-gradient-to-r from-cyan-400 via-sky-300 to-blue-500 bg-clip-text text-transparent">
          We Use
        </span>
      </h2>

      <p className="text-gray-400 mt-5 max-w-2xl mx-auto text-lg">
        We build modern, secure and scalable websites & applications using the
        latest technologies trusted by businesses worldwide.
      </p>
    </div>

    {/* Technologies Grid */}
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
      {[
        { name: "React JS", icon: Sparkles, color: "text-[#61DAFB]" },
        { name: "WordPress", icon: Layout, color: "text-[#21759B]" },
        { name: "PHP", icon: FileCode, color: "text-[#777BB4]" },
        { name: "Firebase", icon: Flame, color: "text-[#FFCA28]" },
        { name: "MySQL", icon: Database, color: "text-[#4479A1]" },
        { name: "Bootstrap", icon: Layers, color: "text-[#7952B3]" },
        { name: "HTML5", icon: Code, color: "text-[#E34F26]" },
        { name: "CSS3", icon: Palette, color: "text-[#1572B6]" },
        { name: "JavaScript", icon: Terminal, color: "text-[#F7DF1E]" },
        { name: "Node JS", icon: Server, color: "text-[#339933]" },
        { name: "Razorpay", icon: CreditCard, color: "text-[#3395FF]" },
        { name: "Cloud", icon: Cloud, color: "text-[#0EA5E9]" },
      ].map((tech, index) => (
        <div
          key={index}
          className="group relative bg-[#0F1629]/80 border border-white/5 rounded-2xl p-4 md:p-6 flex flex-col justify-center items-center gap-3 md:gap-4 backdrop-blur-lg transition-all duration-500 hover:-translate-y-2 hover:border-[#0EA5E9]/50 hover:bg-[#0EA5E9]/5 hover:shadow-[0_10px_30px_-10px_rgba(14,165,233,0.3)] cursor-default overflow-hidden"
        >
          {/* Subtle background glow on hover */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-20 bg-[#0EA5E9]/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
          
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 z-10">
            <tech.icon className={`w-6 h-6 md:w-7 md:h-7 ${tech.color}`} />
          </div>
          
          <h3 className="text-white text-sm md:text-base font-bold text-center group-hover:text-white transition-colors z-10">
            {tech.name}
          </h3>
        </div>
      ))}
    </div>

  </div>
</section>


         {/* 10. Frequently Asked Questions */}
         <section className="py-20 bg-black/30 border-y border-white/5">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold mb-4">Frequently Asked <span className="text-[#0EA5E9]">Questions</span></h2>
               </div>

               <div className="space-y-4">
                  {[
                     { q: "How much does an ecommerce website cost?", a: "It depends on your requirements." },
                     { q: "How many products can I upload?", a: "Unlimited." },
                     { q: "Can I accept online payments?", a: "Yes." },
                     { q: "Is GST invoice available?", a: "Yes." },
                     { q: "Is mobile app included?", a: "Optional." },
                     { q: "Will it be SEO Friendly?", a: "Yes." },
                     { q: "Do you provide support?", a: "Yes." }
                  ].map((faq, idx) => (
                     <FAQItem key={idx} question={faq.q} answer={faq.a} />
                  ))}
               </div>
            </div>
         </section>



      </div>
   );
};

export default EcommerceWebsite;
